/**
 * Lokalhyresavtal · Version 2026 (Formulär R-02) — IR twin of the legacy
 * `lokalhyra` v1 template.
 *
 * Every § is auto-numbered: nothing at the top level hides, so the sequence
 * is the legacy 1–32, while optional sub-clauses (biutrymmen,
 * byggvarudeklaration, oförutsedda kostnader) renumber their siblings.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { LOKALHYRA_BLOCKS } from '../lokalhyra/blocks.js';
const TITLE = 'Lokalhyresavtal · Version 2026';
const FORM_NUMBER = 'Formulär R-02';
const PREAMBLE = 'Mellan hyresvärd och hyresgäst har följande avtal ingåtts.';
export const LOKALHYRA_PARTY_SLOTS = [
    { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
    { id: 'lessee', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
    {
        id: 'lessee_co',
        label: 'Medhyresgäst',
        role: 'lessee',
        ordinal: 1,
        signs: true,
        help: 'Valfritt — fyll i om avtalet har en andra hyresgäst.',
    },
    {
        id: 'guarantor',
        label: 'Borgensman',
        role: 'guarantor',
        ordinal: 0,
        signs: true,
        help: 'Valfritt — fylls i om säkerhetstypen är "Proprieborgen".',
    },
];
export const LOKALHYRA_PROPERTY_SLOTS = [
    { id: 'hyresobjekt', label: 'Hyresobjekt', multi: false, required: true },
];
/** `[instanceId, blockKey]` in document order; instanceId == the legacy section ref. */
const BODY = [
    ['hyresvard', 'lokalhyra.hyresvard'],
    ['hyresgast', 'lokalhyra.hyresgast'],
    ['hyresobjekt', 'lokalhyra.hyresobjekt'],
    ['hyrestid-uppsagning', 'lokalhyra.hyrestid-uppsagning'],
    ['anvandning', 'lokalhyra.anvandning'],
    ['skick', 'lokalhyra.skick'],
    ['hyra', 'lokalhyra.hyra'],
    ['arlig-hojning', 'lokalhyra.arlig-hojning'],
    ['forbrukning', 'lokalhyra.forbrukning'],
    ['avfallshantering', 'lokalhyra.avfallshantering'],
    ['bevakning', 'lokalhyra.bevakning'],
    ['sakerhet', 'lokalhyra.sakerhet'],
    ['besittningsskydd', 'lokalhyra.besittningsskydd'],
    ['las-stold', 'lokalhyra.las-stold'],
    ['underhall', 'lokalhyra.underhall'],
    ['ombyggnad', 'lokalhyra.ombyggnad'],
    ['skyltning', 'lokalhyra.skyltning'],
    ['ledningar', 'lokalhyra.ledningar'],
    ['myndighetsforeskrifter', 'lokalhyra.myndighetsforeskrifter'],
    ['brandskydd', 'lokalhyra.brandskydd'],
    ['utrymningsvagar', 'lokalhyra.utrymningsvagar'],
    ['skatter-avgifter', 'lokalhyra.skatter-avgifter'],
    ['overlatelse', 'lokalhyra.overlatelse'],
    ['storningar', 'lokalhyra.storningar'],
    ['tilltrade', 'lokalhyra.tilltrade'],
    ['forsakring', 'lokalhyra.forsakring'],
    ['konkurs', 'lokalhyra.konkurs'],
    ['force-majeure', 'lokalhyra.force-majeure'],
    ['andringar', 'lokalhyra.andringar'],
    ['lagval-tvist', 'lokalhyra.lagval-tvist'],
    ['bilagor', 'lokalhyra.bilagor'],
    ['underskrift', 'lokalhyra.underskrift'],
];
/** Per-instance layout input: § 2 Hyresgäst sits beside § 1 Hyresvärd, as in the legacy grid. */
const INPUTS = {
    hyresgast: { beside_previous: true },
};
/** The legacy create-time defaults, read off the slots' own `default` (option codes for selects). */
export const LOKALHYRA_DEFAULTS = applyDefaults({
    fields: LOKALHYRA_BLOCKS.flatMap((block) => block.slots),
});
export const lokalhyraV1 = (values = {}) => {
    const body = BODY.map(([instanceId, blockKey]) => ({ instanceId, blockRef: `${blockKey}@v1`, input: INPUTS[instanceId] ?? {} }));
    return {
        schema: COMPOSITION_SCHEMA,
        version: COMPOSITION_VERSION,
        documentKey: 'lokalhyra',
        documentVersion: 'v1',
        title: TITLE,
        locale: 'sv-SE',
        meta: {
            category: 'Lokalhyra',
            numberStyle: 'paragraph',
            shortTitle: 'Lokalhyresavtal',
            formNumber: FORM_NUMBER,
            preamble: PREAMBLE,
            statute: '12 kap. jordabalken',
            signingMode: 'digital',
            partySlots: LOKALHYRA_PARTY_SLOTS,
            propertySlots: LOKALHYRA_PROPERTY_SLOTS,
        },
        values: { ...LOKALHYRA_DEFAULTS, ...values },
        blocks: [
            { instanceId: 'letterhead', blockRef: 'common.letterhead@v1', slot: 'letterhead', input: { title: TITLE, form_number: FORM_NUMBER } },
            { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: { form_number: FORM_NUMBER } },
            { instanceId: 'avtalsuppgifter', blockRef: 'lokalhyra.avtalsuppgifter@v1', input: {} },
            { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE, variant: 'intro' } },
            ...body,
        ],
    };
};
//# sourceMappingURL=lokalhyra.js.map